// Activate confirmation modal for Draft SOWs. Mirrors the variation
// approve dialog shape: re-states the SOW's substance (name, number,
// period, value) before the user confirms. On confirm, calls the
// standard PUT path with `{status: 'active', version}` — the backend's
// state-machine guard accepts the Draft → Active transition and the
// updateSowStatus repo method persists the status atomically with a
// version bump.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import { formatSowCurrency } from "./formatSowCurrency";
import { isConflictError } from "../../../../services/api";
import type { Sow } from "../../../../types";

const ALREADY_TRANSITIONED_COPY =
  "This SOW has already been transitioned. Refresh the page to see the latest status.";

interface ActivateSowDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sow: Sow | null;
  gstApplies: boolean | undefined;
  onConfirm: () => Promise<unknown>;
  isSubmitting?: boolean;
}

function fmtDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(`${v}T00:00:00`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return v;
  }
}

export default function ActivateSowDialog({
  isOpen,
  onClose,
  sow,
  gstApplies,
  onConfirm,
  isSubmitting,
}: ActivateSowDialogProps) {
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      if (isConflictError(err)) {
        setError(ALREADY_TRANSITIONED_COPY);
        return;
      }
      setError(
        err instanceof Error ? err.message : "Failed to activate SOW.",
      );
    }
  };

  const formatted = sow
    ? formatSowCurrency(sow.originalValue, gstApplies)
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={sow ? `Activate ${sow.number}?` : "Activate SOW"}
    >
      <div className="space-y-4">
        {sow && (
          <>
            <p className="text-body text-slate-600 dark:text-slate-300">
              Activating this SOW marks it as in-flight under the contract
              and includes its value in current contract calculations.
            </p>
            <dl
              data-testid="activate-sow-summary"
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md bg-slate-50 dark:bg-slate-800/60 p-3"
            >
              <div className="sm:col-span-2">
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  SOW
                </dt>
                <dd className="text-body text-slate-900 dark:text-slate-100">
                  {sow.name}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Period
                </dt>
                <dd className="text-body text-slate-900 dark:text-slate-100">
                  {fmtDate(sow.startDate)} – {fmtDate(sow.endDate)}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Value
                </dt>
                <dd className="text-body text-slate-900 dark:text-slate-100 tabular-nums">
                  {formatted?.value}{" "}
                  <span className="text-caption text-slate-500 dark:text-slate-400">
                    {formatted?.suffix}
                  </span>
                </dd>
              </div>
            </dl>
          </>
        )}
        {error && <Alert variant="error">{error}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={isSubmitting || !sow}
            data-testid="activate-sow-confirm"
          >
            Activate
          </Button>
        </div>
      </div>
    </Modal>
  );
}
